import { boundMethod } from 'autobind-decorator';
import { PhoneNumber, SaveStatus } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import _isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { PureComponent } from 'react';

import {
  type FlowExtraData,
  FlowExtraDataFetch,
} from 'components/powerFlowShared/components/extraDataFetch';
import {
  FiltersNodePhone,
  IntegrationNodeCall,
  OpeningHoursNode,
  TagsNode,
} from 'components/powerFlowShared/components/nodeTypes';
import { BusinessHoursPhoneNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/businesshours';
import { FiltersNodePhoneAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/filters';
import { InitNodePhoneAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/init';
import { IntegrationNodePhoneAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/integration';
import { OpeningHoursDeprecatedNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/openingHours';
import { QueueNodePhoneAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/queue';
import { TagsNodeAttributes } from 'components/powerFlowShared/components/nodeTypes/canvas/tags';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getActionNodes } from 'components/powerFlowShared/helpers/getInvalidNodes';
import { FlowEditorChrome, FlowType } from 'fable/flowEditor';
import { RouteComponentProps } from 'hocs/router';

import type { FlowCallActionT } from '../api/types';
import type { AnnouncementT } from '../components/types';
import type {
  FlowCallDispatchPropsT,
  FlowCallStatePropsT,
} from '../containers/powerCallFlow';
import TopbarButtons from '../containers/topbarButtons';

import {
  AudioMessageNode,
  CallbackNode,
  DialInputNode,
  ForwardNode,
  InitNode,
  IVRNode,
  LocationsNode,
  QueueNode,
  RecordConversationNode,
  VoicemailNode,
} from './nodeTypes';
import { AudioMessageNodeAttributes } from './nodeTypes/canvas/audioMessage';
import { CallbackNodeAttributes } from './nodeTypes/canvas/callback';
import { DialInputNodeAttributes } from './nodeTypes/canvas/dialInput';
import { ForwardNodeAttributes } from './nodeTypes/canvas/forward';
import { IVRNodeAttributes } from './nodeTypes/canvas/ivr';
import { LocationsNodeAttributes } from './nodeTypes/canvas/locations';
import { RecordConversationNodeAttributes } from './nodeTypes/canvas/recordConversation';
import { VoicemailNodeAttributes } from './nodeTypes/canvas/voicemail';

import '../styles/call-flow.less';

export type PowerCallFlowExtraDataT = {
  number: string;
  agentDisplayName?: null | string;
  announcements: AnnouncementT[];
} & FlowExtraData;

export type FlowCallOwnPropsT = RouteComponentProps<'flowId'>;

type FlowCallPropsT = FlowCallOwnPropsT &
  FlowCallStatePropsT &
  FlowCallDispatchPropsT;

type FlowCallStateT = {
  actions: null | FlowCallActionT[];
  active: boolean;
  errorMessage: null | string;
  invalidNodes: null | string[];
  name: string;
  nodeTypes: {};
  number: string;
  ready: boolean;
  saveStatus: SaveStatus;
};

class PowerCallFlow extends PureComponent<FlowCallPropsT, FlowCallStateT> {
  constructor(props: FlowCallPropsT) {
    super(props);

    const { isCanvasEnabled, isIntegrationsEnabled } = props;

    this.state = {
      actions: null,
      active: true,
      errorMessage: null,
      invalidNodes: null,
      name: '',
      nodeTypes: isCanvasEnabled
        ? {
            init: InitNodePhoneAttributes,
            // GREEN - Termination -> Queue
            queue: QueueNodePhoneAttributes,
            forward: ForwardNodeAttributes,
            voicemail: VoicemailNodeAttributes,
            // RED - Termination -> Closed
            announcement: AudioMessageNodeAttributes,
            // PURPLE - Splitters
            externaldatarouting: FiltersNodePhoneAttributes,
            phonenumber: LocationsNodeAttributes,
            ivrmenu: IVRNodeAttributes,
            [FlowNodeTypes.OPENING_HOURS]: OpeningHoursDeprecatedNodeAttributes,
            [FlowNodeTypes.BUSINESS_HOURS]: BusinessHoursPhoneNodeAttributes,
            // ORANGE - Input required
            callback: CallbackNodeAttributes,
            externaldataphoneinput: DialInputNodeAttributes,
            // BLUE - Other
            callrecording: RecordConversationNodeAttributes,
            ...(isIntegrationsEnabled
              ? { [FlowNodeTypes.INTEGRATION]: IntegrationNodePhoneAttributes }
              : {}),
            tags: TagsNodeAttributes,
          }
        : {
            init: InitNode,
            // GREEN - Termination -> Queue
            queue: QueueNode, // (!Q)ueue
            forward: ForwardNode, // (F)orward
            voicemail: VoicemailNode, // (V)oicemail
            // RED - Termination -> Closed
            announcement: AudioMessageNode,
            // PURPLE - Splitters
            externaldatarouting: FiltersNodePhone, // (B)ranch
            phonenumber: LocationsNode, // (C)aller Routing
            ivrmenu: IVRNode, // (I)VR
            openinghours: OpeningHoursNode, // (O)pening Hours
            // ORANGE - Input required
            callback: CallbackNode, // (C)allBack
            externaldataphoneinput: DialInputNode, // (D)ial input
            // BLUE - Other
            callrecording: RecordConversationNode, // (C)all recording
            ...(isIntegrationsEnabled
              ? { [FlowNodeTypes.INTEGRATION]: IntegrationNodeCall }
              : {}),
            tags: TagsNode,
          },
      number: '',
      ready: false,
      saveStatus: SaveStatus.Saved,
    };
  }

  static getDerivedStateFromProps(
    props: FlowCallPropsT,
    state: FlowCallStateT,
  ) {
    if (props.status === 'complete' && !state.number) {
      // flow is ready, and hasn't been initialized yet..
      // we update flow info in state
      return {
        actions: props.flowData.actions,
        active: props.flowData.active,
        name: props.flowData.name,
        number: props.flowData.number,
        ready: true,
        saveStatus: SaveStatus.Saved,
      };
    }
    if (props.status === 'error') {
      return { saveStatus: SaveStatus.Failed };
    }
    if (
      props.status === 'complete' &&
      (state.saveStatus === SaveStatus.Saving ||
        state.saveStatus === SaveStatus.Failed)
    ) {
      return { saveStatus: SaveStatus.Saved };
    }

    // no change
    return null;
  }

  componentDidMount() {
    this.props.fetchFlow(this.props.id);
    this.props.getNumbersByQuery({ query: { limit: '15' } });
    this.props.getAllNumbers();
    this.props.getAllAudios();
    this.props.flowGetLanguages({
      flowID: this.props.id || 'local',
    });
  }

  componentWillUnmount() {
    this.props.flushFlow();
  }

  @boundMethod
  hasUnsavedChanges() {
    const { saveStatus } = this.state;
    const { id } = this.props;

    return !id || saveStatus <= 0;
  }

  @boundMethod
  handleNumberChange(phoneNumber: PhoneNumber): void {
    if (this.state.number !== phoneNumber.e164_format) {
      ampli.flowPhoneNumberChanged();

      this.setState({
        number: phoneNumber.e164_format,
        saveStatus: SaveStatus.Unsaved,
      });
    }
  }

  @boundMethod
  handleOnChange({
    name,
    actions,
    active,
  }: {
    name: string;
    actions: null | FlowCallActionT[];
    active: boolean;
  }) {
    if (
      !this.hasUnsavedChanges() ||
      this.state.name !== name ||
      this.state.actions !== actions ||
      this.state.active !== active
    ) {
      this.setState({
        actions,
        active,
        invalidNodes: null,
        name,
        saveStatus: SaveStatus.Unsaved,
      });
    }
  }

  @boundMethod
  deleteLocally() {
    this.props.removeLocalFlow();
  }

  @boundMethod
  getFullFlow(isLocal = false) {
    const { name, actions, active, number } = this.state;
    const { id = 'local', agentDisplayName } = this.props;

    return this.getFullFlowMemo(
      isLocal,
      id || 'local',
      name,
      actions,
      active,
      number,
      agentDisplayName,
    );
  }

  getFullFlowMemo = memoize(
    (
      isLocal: boolean,
      id: string,
      name: string,
      actions: null | FlowCallActionT[],
      active: boolean,
      number: string,
      agentDisplayName?: null | string,
    ) => {
      return {
        id,
        location: isLocal ? 'local' : number,
        number: isLocal ? 'local' : number,
        name: name || 'Name your flow',
        actions: actions || [],
        active: isLocal ? false : active,
        updated_by: agentDisplayName || null,
        channel: 'pstn_phone' as const,
      };
    },
  );

  @boundMethod
  handleOnSave() {
    const { number, actions } = this.state;
    const invalidNodes = actions && getActionNodes(actions);

    if (invalidNodes?.length) {
      this.setState({
        errorMessage: null,
        invalidNodes,
      });
    } else if (!number || number === 'local') {
      this.setState({
        errorMessage: 'No number selected',
      });
    } else {
      this.setState(
        {
          errorMessage: null,
          invalidNodes: null,
          saveStatus: SaveStatus.Saving,
        },
        this.updateFlow,
      );
    }
  }

  @boundMethod
  updateFlow() {
    const { flowData } = this.props;
    const fullFlow = this.getFullFlow();

    // this.props[method](fullFlow, flowData.actions);
    this.props.updateFlow(fullFlow, flowData.actions);
    this.deleteLocally();
  }

  @boundMethod
  handleOnActivate() {
    this.setState(
      {
        active: true,
        errorMessage: null,
      },
      this.updateFlow,
    );
  }

  render() {
    const { announcements, flowData, agentDisplayName } = this.props;
    const { ready, number, invalidNodes, actions, errorMessage, nodeTypes } =
      this.state;

    return (
      <FlowExtraDataFetch
        actions={actions}
        render={(extraData) => (
          <div className="powercallflow-container">
            {ready && (
              <FlowEditorChrome
                errorMessage={errorMessage}
                extraData={{
                  number,
                  announcements,
                  agentDisplayName: agentDisplayName ?? '',
                  ...extraData,
                }}
                flowType={FlowType.FLOW_TYPE_CALL}
                goBackPath="/settings/phone"
                hasUnsavedChanges={this.hasUnsavedChanges()}
                initialData={flowData}
                invalidNodes={invalidNodes}
                nodeTypes={nodeTypes}
                onActivate={this.handleOnActivate}
                onChange={this.handleOnChange}
                onSave={this.handleOnSave}
                topleftButtonComponent={TopbarButtons}
                topleftButtonComponentProps={{
                  changeNumber: this.handleNumberChange,
                }}
              />
            )}
          </div>
        )}
      />
    );
  }
}

export default PowerCallFlow;
